import React, { useEffect, useRef, useState } from 'react';

/**
 * YouTubePreview — an embedded YouTube player (via the IFrame Player API) with a
 * volume slider (0–100) + mute toggle, so a customer can preview a song at a
 * comfortable level rather than only muting/unmuting a plain <iframe>.
 *
 * NOTE: Mobile operating systems (iOS/Android) generally ignore programmatic
 * volume changes — media volume is hardware-only there — so `setVolume` mainly
 * affects desktop browsers. Mute/unmute still works everywhere.
 */

// Minimal typings so `window.YT` / `window.onYouTubeIframeAPIReady` typecheck under strict TS.
interface YTPlayer {
  setVolume: (v: number) => void;
  getVolume?: () => number;
  mute: () => void;
  unMute: () => void;
  destroy: () => void;
}
interface YTPlayerConstructor {
  new (el: HTMLElement, opts: {
    videoId: string;
    playerVars?: Record<string, unknown>;
    events?: { onReady?: (e: { target: YTPlayer }) => void };
  }): YTPlayer;
}
declare global {
  interface Window {
    YT?: { Player?: YTPlayerConstructor };
    onYouTubeIframeAPIReady?: () => void;
  }
}

const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

/** Load the IFrame API once app-wide and resolve when `window.YT.Player` is ready. */
function loadYouTubeApi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.YT?.Player) {
      resolve();
      return;
    }
    // Chain onto any existing ready callback so we don't clobber another loader.
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try { prev?.(); } catch { /* noop */ }
      resolve();
    };
    // Inject the script only once (guard against duplicate <script> tags).
    if (!document.querySelector(`script[src="${IFRAME_API_SRC}"]`)) {
      const s = document.createElement('script');
      s.src = IFRAME_API_SRC;
      s.async = true;
      document.head.appendChild(s);
    }
    // Poll as a fallback in case the global callback was already consumed by an earlier load.
    const started = Date.now();
    const iv = setInterval(() => {
      if (window.YT?.Player) {
        clearInterval(iv);
        resolve();
      } else if (Date.now() - started > 8000) {
        clearInterval(iv);
        reject(new Error('YouTube IFrame API load timed out')); // caller falls back to a plain iframe
      }
    }, 200);
  });
}

const YouTubePreview: React.FC<{ videoId: string }> = ({ videoId }) => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const [volume, setVolume] = useState(60);
  const [muted, setMuted] = useState(false);
  const [failed, setFailed] = useState(false);
  // Keep the latest volume without re-running the player effect on every slider move.
  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  useEffect(() => {
    let cancelled = false;
    setFailed(false);

    loadYouTubeApi()
      .then(() => {
        if (cancelled || !mountRef.current) return;
        const Ctor = window.YT?.Player;
        if (!Ctor) { setFailed(true); return; }
        try {
          playerRef.current = new Ctor(mountRef.current, {
            videoId,
            playerVars: { playsinline: 1, rel: 0 },
            events: {
              onReady: (e) => {
                try {
                  e.target.setVolume(volumeRef.current);
                  if (muted) e.target.mute();
                } catch { /* noop */ }
              },
            },
          });
        } catch {
          setFailed(true);
        }
      })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => {
      cancelled = true;
      try { playerRef.current?.destroy(); } catch { /* noop */ }
      playerRef.current = null;
    };
    // Recreate the player whenever the video changes. `muted` is intentionally read
    // at onReady time only (via closure) — toggling it later drives the player directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  const onSlide = (v: number) => {
    setVolume(v);
    const p = playerRef.current;
    if (!p) return;
    try {
      p.setVolume(v);
      if (v > 0 && muted) { p.unMute(); setMuted(false); }
    } catch { /* noop */ }
  };

  const toggleMute = () => {
    const p = playerRef.current;
    setMuted((m) => {
      const next = !m;
      try { if (next) p?.mute(); else p?.unMute(); } catch { /* noop */ }
      return next;
    });
  };

  // If the API failed to load, fall back to a plain embed so the preview still works.
  if (failed) {
    return (
      <div style={wrap}>
        <iframe
          style={frame}
          src={`https://www.youtube.com/embed/${videoId}`}
          title="Song preview"
          allow="encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div>
      <div style={wrap}>
        <div ref={mountRef} style={frame} />
      </div>
      <div style={controls}>
        <button type="button" style={muteBtn} onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
          {muted ? '🔇' : '🔊'}
        </button>
        <input
          type="range"
          min={0}
          max={100}
          value={muted ? 0 : volume}
          onChange={(e) => onSlide(Number(e.target.value))}
          style={slider}
          aria-label="Preview volume"
        />
        <span style={volLabel}>{muted ? 0 : volume}</span>
      </div>
    </div>
  );
};

const wrap: React.CSSProperties = { marginTop: 12, width: '100%', aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden', background: '#000' };
const frame: React.CSSProperties = { width: '100%', height: '100%', border: 'none', display: 'block' };
const controls: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 };
const muteBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 0 };
const slider: React.CSSProperties = { flex: 1, accentColor: '#7c3aed', cursor: 'pointer' };
const volLabel: React.CSSProperties = { fontSize: '0.78rem', color: '#6b7280', minWidth: 26, textAlign: 'right' };

export default YouTubePreview;
